"use client"

import { useState } from "react"

import type { SalesSection } from "@/app/course/[slug]/course-landing.controller"

import { lessonMeta } from "./format"
import { SalesIcon } from "./icons"

/**
 * Acordeão de conteúdo: um módulo aberto por vez, primeiro aberto por
 * padrão. Módulo sem aulas fica colapsado (título + contagem), sem chevron.
 * Todos os módulos aparecem — sem rodapé "+N módulos".
 */
export function CurriculumAccordion({
  sections,
}: {
  sections: SalesSection[]
}) {
  const [open, setOpen] = useState(0)

  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-sales-line bg-white sm:mt-11">
      {sections.map((section, i) => {
        const hasLessons = section.lessons.length > 0
        const isOpen = hasLessons && open === i
        return (
          <div
            key={section.id}
            className={i > 0 ? "border-t border-sales-line" : undefined}
          >
            <button
              type="button"
              onClick={() => hasLessons && setOpen(isOpen ? -1 : i)}
              aria-expanded={hasLessons ? isOpen : undefined}
              className={`flex min-h-[68px] w-full items-center gap-3 px-5 text-left sm:gap-4 sm:px-7 ${
                isOpen ? "bg-sales-bg-alt" : "bg-transparent"
              } ${hasLessons ? "cursor-pointer" : "cursor-default"}`}
            >
              <span className="shrink-0 font-mono text-[10.5px] text-sales-text-mute">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-heading text-[15px] leading-[1.25] font-semibold tracking-[-0.018em] text-sales-ink sm:text-[16.5px]">
                  {section.title}
                </span>
                <span className="mt-0.5 block text-[11.5px] text-sales-text-mid">
                  {`${section.lessons.length} ${section.lessons.length === 1 ? "aula" : "aulas"}`}
                </span>
              </span>
              {hasLessons && (
                <SalesIcon.chevron
                  width={15}
                  height={15}
                  className={`shrink-0 text-sales-text-mid transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              )}
            </button>
            {isOpen && (
              <ul className="bg-sales-bg-alt px-5 pb-5 sm:px-7">
                {section.lessons.map((lesson) => {
                  const Icon =
                    lesson.type === "video" ? SalesIcon.play : SalesIcon.file
                  return (
                    <li
                      key={lesson.id}
                      className="flex items-center gap-3 border-t border-sales-line-strong py-2.5"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-sales-line bg-white text-sales-text-mid">
                        <Icon width={10} height={10} />
                      </span>
                      <span className="min-w-0 flex-1 text-[13px] text-sales-ink-soft">
                        {lesson.title}
                      </span>
                      <span className="shrink-0 font-mono text-[10.5px] text-sales-text-mid tabular-nums">
                        {lessonMeta(lesson)}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}
